import { Link } from 'react-router-dom';
import {
  Table, THead, TBody, TR, TH, TD, StatusBadge, EmptyState, ErrorNotice, Loading, Pagination,
} from './ui/index.js';
import { formatMoney, formatTimestamp, truncateId } from '../lib/format.js';

/** PL-1's seven statuses, for every screen that filters a list of payments by one. */
export const PAYMENT_STATUS_OPTIONS = [
  ['', 'Any status'], ['pending', 'Pending'], ['processing', 'Processing'], ['succeeded', 'Succeeded'],
  ['failed', 'Failed'], ['cancelled', 'Cancelled'], ['refunded', 'Refunded'], ['partially_refunded', 'Partly refunded'],
];

/**
 * FE-9: the four states, in one place, so every screen that lists payments
 * handles them identically. §13 requires every row to link to its detail page.
 */
export function PaymentsTable({ query, onPageChange, emptyState }) {
  const { data, error, loading, refetch } = query;

  if (loading) return <Loading label="Loading payments…" />;
  if (error) return <ErrorNotice error={error} onRetry={refetch} title="Could not load payments" className="m-4" />;
  if (!data?.payments?.length) return emptyState ?? <EmptyState title="No payments found" description="Try adjusting your filters." />;

  return (
    <>
      <Table>
        <THead>
          <TR>
            <TH>Payment</TH>
            <TH>Order</TH>
            <TH>Description</TH>
            <TH>Customer</TH>
            <TH align="right">Amount</TH>
            <TH>Status</TH>
            <TH align="right">Created</TH>
          </TR>
        </THead>
        <TBody>
          {data.payments.map((payment) => (
            <TR key={payment.id}>
              <TD>
                <Link to={`/payments/${payment.id}`} className="font-mono text-xs font-medium text-accent-ink hover:underline">
                  {truncateId(payment.id)}
                </Link>
                {payment.card?.last4 && (
                  <div className="mt-0.5 text-[11px] capitalize text-muted">
                    {payment.card.brand} ···· {payment.card.last4}
                  </div>
                )}
              </TD>
              <TD className="text-xs text-ink-soft">{payment.orderId ?? <span className="text-faint">—</span>}</TD>
              {/* Shown because it is what the gateway sees on the transaction. */}
              <TD className="max-w-[16rem] truncate text-xs text-ink-soft" title={payment.description || undefined}>
                {payment.description || <span className="text-faint">—</span>}
              </TD>
              <TD className="max-w-[14rem] truncate text-xs text-ink-soft">
                {payment.customer?.email ?? <span className="text-faint">—</span>}
              </TD>
              <TD align="right" className="tabular whitespace-nowrap font-medium">
                {formatMoney(payment.amount, payment.currency)}
                {payment.amountRefunded > 0 && (
                  <div className="text-[11px] font-normal text-muted">
                    −{formatMoney(payment.amountRefunded, payment.currency)} refunded
                  </div>
                )}
              </TD>
              <TD><StatusBadge status={payment.status} size="sm" /></TD>
              <TD align="right" className="whitespace-nowrap text-xs text-muted">
                {formatTimestamp(payment.createdAt)}
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>

      <Pagination total={data.total} limit={data.limit} skip={data.skip} onChange={onPageChange} />
    </>
  );
}

export default PaymentsTable;
